import { useState, type ReactNode } from 'react';
import {
  FURNITURE,
  FURNITURE_BY_ID,
  FURNITURE_CATEGORIES,
  NEEDS,
  footprint,
  placementProblem,
  type FurnitureCategory,
  type FurnitureItem,
  type OwnedThing,
} from '@london-life/contracts';
import type { RoomThing } from '@london-life/game/house/room';

// Buy mode's parts, shared by your own home and a decorator working on a copy of someone else's: where a new thing
// goes, the catalogue (what is in the room, what is stored, the shop) and the bar for the thing picked up.

// the first clear spot for a thing, searched from the open corner inwards
export function freeSpot(
  floor: { w: number; d: number },
  placed: RoomThing[],
  item: FurnitureItem,
): { x: number; z: number } | null {
  const { w, d } = floor;
  const foot = footprint(item, 0);
  for (let z = d / 2 - foot.d / 2; z >= -d / 2 + foot.d / 2; z -= 0.25)
    for (let x = w / 2 - foot.w / 2; x >= -w / 2 + foot.w / 2; x -= 0.25) {
      const spot = { x: Math.round(x * 100) / 100, z: Math.round(z * 100) / 100 };
      if (!placementProblem(floor, [...placed, { id: 'new', item: item.id, ...spot, rotation: 0 }])) return spot;
    }
  return null;
}
export const placedOf = (things: OwnedThing[]): RoomThing[] =>
  things.flatMap((thing) => (thing.placed ? [{ id: thing.id, item: thing.item, ...thing.placed }] : []));
const effectOf = (item: FurnitureItem) =>
  item.need ? `${NEEDS[item.need].icon} +${item.restores} ${NEEDS[item.need].label}` : null;
const sizeOf = (item: FurnitureItem) =>
  `${Math.max(1, Math.round(item.size.w * 2))}×${Math.max(1, Math.round(item.size.d * 2))}`;

// one thing as a card: its size, stars, picture, name, what it does, and a line under it
export function ShopCard({
  item,
  extra,
  onClick,
  disabled = false,
}: {
  item: FurnitureItem;
  extra: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button type="button" className="shop-card" onClick={onClick} disabled={disabled}>
      <span className="shop-card-top">
        <small>{sizeOf(item)}</small>
        <small className="shop-stars" aria-label={`${item.tier} star${item.tier === 1 ? '' : 's'}`}>
          {'★'.repeat(item.tier)}
        </small>
      </span>
      <span className="shop-card-icon" aria-hidden="true">
        {item.icon}
      </span>
      <strong>{item.label}</strong>
      {effectOf(item) && <small className="shop-card-effect">{effectOf(item)}</small>}
      {extra}
    </button>
  );
}

type Shelf = 'room' | 'stored' | FurnitureCategory;

// the catalogue: what is in the room (tap to pick up), what is stored (place it), and the shop by category
export function Catalogue({
  inRoom,
  stored,
  busy,
  price,
  onPick,
  onPlace,
  onBuy,
  onHide,
  hint,
  shopNote,
  firstShelf = 'room',
}: {
  inRoom: RoomThing[];
  stored: { id: string; item: string }[];
  busy: boolean;
  // a shop card's price line, and whether it can be had now
  price: (item: FurnitureItem) => { label: string; short: boolean };
  onPick: (id: string) => void;
  onPlace: (thing: { id: string; item: string }) => void;
  // missing: no shop (only what is there already)
  onBuy?: (item: FurnitureItem) => void;
  onHide: () => void;
  hint: string;
  // shown instead of the shop when there is none
  shopNote?: string;
  firstShelf?: Shelf;
}) {
  const [shelf, setShelf] = useState<Shelf>(firstShelf);
  const shelves: [Shelf, string][] = [
    ['room', `In the room (${inRoom.length})`],
    ['stored', `Stored (${stored.length})`],
    ...(Object.keys(FURNITURE_CATEGORIES) as FurnitureCategory[]).map(
      (key) => [key, FURNITURE_CATEGORIES[key]] as [Shelf, string],
    ),
  ];
  return (
    <section className="venue-bar catalogue" aria-label="Catalogue">
      <header className="catalogue-head">
        <h2>Catalogue</h2>
        <button type="button" onClick={onHide}>
          Hide
        </button>
      </header>
      <div className="venue-tabs" role="tablist" aria-label="Catalogue sections">
        {shelves.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={shelf === key}
            aria-pressed={shelf === key}
            onClick={() => setShelf(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div
        className="shop-grid"
        aria-label={shelf === 'room' ? 'What is in the room' : shelf === 'stored' ? 'Things stored' : 'Things for sale'}
      >
        {shelf === 'room' &&
          inRoom.map((thing) => (
            <ShopCard
              key={thing.id}
              item={FURNITURE_BY_ID.get(thing.item)!}
              extra={<em className="shop-card-note">Tap to pick up</em>}
              onClick={() => onPick(thing.id)}
            />
          ))}
        {shelf === 'stored' &&
          (stored.length ? (
            stored.map((thing) => (
              <ShopCard
                key={thing.id}
                item={FURNITURE_BY_ID.get(thing.item)!}
                extra={<em className="shop-card-note">Place it</em>}
                onClick={() => onPlace(thing)}
                disabled={busy}
              />
            ))
          ) : (
            <p className="place-soon">Nothing stored. Everything is in the room.</p>
          ))}
        {shelf !== 'room' &&
          shelf !== 'stored' &&
          (onBuy ? (
            FURNITURE.filter((item) => item.category === shelf).map((item) => {
              const { label, short } = price(item);
              return (
                <ShopCard
                  key={item.id}
                  item={item}
                  extra={<b className={short ? 'shop-price short' : 'shop-price'}>{label}</b>}
                  onClick={() => onBuy(item)}
                  disabled={busy || short}
                />
              );
            })
          ) : (
            <p className="place-soon">{shopNote}</p>
          ))}
      </div>
      <p className="house-hint">{hint}</p>
    </section>
  );
}

// the thing picked up: its name, a line about it, and what can be done with it
export function SelectedBar({ item, line, children }: { item: FurnitureItem; line: string; children: ReactNode }) {
  return (
    <section className="venue-bar buy-selected" aria-label={`Selected: ${item.label}`}>
      <header className="venue-head">
        <span className="shop-card-icon" aria-hidden="true">
          {item.icon}
        </span>
        <div className="venue-where">
          <h2>{item.label}</h2>
          <p>{line}</p>
        </div>
      </header>
      {children}
    </section>
  );
}
